import CheckInResultModal, {
  type CheckInResultStatus,
  type CheckInTicketData,
} from '@/components/checkin/CheckInResultModal';
import QRScanner from '@/components/checkin/QRScanner';
import { Skeleton } from '@/components/ui/Skeleton';
import { api } from '@/lib/api';
import { ArrowLeft, BarChart3, Clock, ScanLine } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface CheckInStats {
  totalTickets: number;
  checkedIn: number;
  pending: number;
  percentage: number;
  recentCheckIns: Array<{
    ticketCode: string;
    customerName: string;
    seatIdentifier: string;
    checkedInAt: string;
    checkedInBy: string;
  }>;
}

interface ModalState {
  isOpen: boolean;
  status: CheckInResultStatus;
  message: string;
  ticket?: CheckInTicketData;
  lastScannedCode?: string;
}

export default function CheckInPage() {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [stats, setStats] = useState<CheckInStats | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [eventName, setEventName] = useState<string>('');
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Estado del modal persistente - NO se resetea por polling
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    status: 'success',
    message: '',
  });

  const audioSuccessRef = useRef<HTMLAudioElement | null>(null);
  const audioErrorRef = useRef<HTMLAudioElement | null>(null);

  // Cargar información del evento
  useEffect(() => {
    if (!eventId) return;

    const loadEvent = async () => {
      try {
        const event = await api.get<any>(`/events/${eventId}`);
        setEventName(event.name);
      } catch (err) {
        console.error('Error loading event:', err);
      }
    };

    loadEvent();
  }, [eventId]);

  // Cargar estadísticas - optimizado para no causar re-renders innecesarios
  const loadStats = useCallback(
    async (showLoading = false) => {
      if (!eventId) return;

      try {
        // Solo mostrar loading en la carga inicial
        if (showLoading) {
          setIsLoadingStats(true);
        }
        const data = await api.get<CheckInStats>(`/checkin/stats/${eventId}`);
        setStats(data);
      } catch (err) {
        console.error('Error loading stats:', err);
      } finally {
        if (showLoading) {
          setIsLoadingStats(false);
        }
      }
    },
    [eventId]
  );

  // Cargar stats al montar y cada 30 segundos (optimizado para no consumir muchos requests)
  useEffect(() => {
    // Carga inicial con loading
    loadStats(true);

    // Polling silencioso cada 30 minutos - NO causa skeleton ni afecta el modal
    const interval = setInterval(
      () => {
        loadStats(false);
      },
      30 * 60 * 1000
    ); // 30 minutos (1,800,000 ms)

    return () => clearInterval(interval);
  }, [eventId, loadStats]);

  // Extraer código de ticket de URL o texto escaneado
  const extractTicketCode = (scannedData: string): string => {
    console.log(' [CheckIn] Datos escaneados:', scannedData);

    // Si es una URL de verificación, extraer el código
    // Formato: https://domain.com/verificar-ticket/WRBI1U_HIUDV (puede tener guiones y guiones bajos)
    const urlMatch = scannedData.match(/\/verificar-ticket\/([A-Z0-9_-]+)$/i);
    if (urlMatch) {
      console.log('🔗 [CheckIn] URL detectada, extrayendo código:', urlMatch[1]);
      return urlMatch[1].toUpperCase();
    }

    // Si no es URL, asumir que es el código directo
    return scannedData.trim().toUpperCase();
  };

  // Manejar escaneo de QR - Abre modal persistente
  const handleScan = useCallback(
    async (scannedData: string) => {
      // Bloquear si ya está escaneando o si el modal está abierto
      if (isScanning || modalState.isOpen || !eventId) return;

      const ticketCode = extractTicketCode(scannedData);

      try {
        setIsScanning(true);
        console.log(' [CheckIn] Validando ticket:', ticketCode, '(escaneado:', scannedData, ')');

        const result = await api.post<any>('/checkin/validate', {
          ticketCode,
          eventId,
        });

        console.log(' [CheckIn] Resultado:', result);

        // Determinar el status para el modal
        let modalStatus: CheckInResultStatus = 'success';
        if (result.status === 'already_checked_in') {
          modalStatus = 'already_checked_in';
          audioErrorRef.current?.play().catch(() => {});
        } else if (result.status === 'success') {
          modalStatus = 'success';
          audioSuccessRef.current?.play().catch(() => {});
        }

        // Abrir modal con resultado - PERSISTENTE hasta acción del usuario
        setModalState({
          isOpen: true,
          status: modalStatus,
          message: result.message || '',
          ticket: result.ticket,
          lastScannedCode: ticketCode,
        });

        // Recargar stats silenciosamente
        loadStats(false);
      } catch (err: any) {
        console.error(' [CheckIn] Error:', err);

        let errorMessage = 'Error al validar el ticket';
        let errorStatus: CheckInResultStatus = 'error';

        if (err.message) {
          errorMessage = err.message;
          // Detectar si es código inválido
          if (
            err.message.toLowerCase().includes('no válido') ||
            err.message.toLowerCase().includes('invalid') ||
            err.message.toLowerCase().includes('no encontrado')
          ) {
            errorStatus = 'invalid';
          }
        }

        audioErrorRef.current?.play().catch(() => {});

        // Abrir modal con error - PERSISTENTE hasta acción del usuario
        setModalState({
          isOpen: true,
          status: errorStatus,
          message: errorMessage,
          lastScannedCode: ticketCode,
        });
      } finally {
        setIsScanning(false);
      }
    },
    [isScanning, modalState.isOpen, eventId, loadStats]
  );

  // Cerrar modal
  const handleCloseModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Escanear siguiente (cierra modal y permite nuevo escaneo)
  const handleScanNext = useCallback(() => {
    setModalState({
      isOpen: false,
      status: 'success',
      message: '',
    });
  }, []);

  // Reintentar el último código escaneado
  const handleRetry = useCallback(() => {
    if (modalState.lastScannedCode) {
      setModalState((prev) => ({ ...prev, isOpen: false }));
      // Pequeño delay para que el modal se cierre antes de re-escanear
      setTimeout(() => {
        handleScan(modalState.lastScannedCode!);
      }, 100);
    }
  }, [modalState.lastScannedCode, handleScan]);

  if (!eventId) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-red-600">ID de evento no válido</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Audio para feedback */}
      <audio ref={audioSuccessRef} src="/success-sound.mp3" preload="auto" />
      <audio ref={audioErrorRef} src="/error-sound.mp3" preload="auto" />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(`/dashboard/events/${eventId}/attendees`)}
            className="text-sm text-gray-600 hover:text-gray-900 mb-2 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Asistentes
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Check-in en Vivo</h1>
          <p className="text-gray-600 mt-1">{eventName}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Panel izquierdo: Escáner */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <ScanLine className="w-5 h-5 text-indigo-600" />
                Escanear Ticket
              </h2>

              <QRScanner onScan={handleScan} isScanning={isScanning} />

              {/* Indicador de validación en progreso */}
              {isScanning && (
                <div className="mt-6 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-600 mt-2">Validando ticket...</p>
                </div>
              )}
            </div>
          </div>

          {/* Panel derecho: Estadísticas */}
          <div className="space-y-6">
            {/* Resumen */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Estadísticas
              </h2>

              {isLoadingStats ? (
                <div className="space-y-4">
                  {/* Skeleton de barra de progreso */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-10" />
                    </div>
                    <Skeleton className="h-3 w-full rounded-full" />
                  </div>

                  {/* Skeleton de números */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="text-center">
                        <Skeleton className="h-9 w-12 mx-auto mb-1" />
                        <Skeleton className="h-3 w-16 mx-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : stats ? (
                <div className="space-y-4">
                  {/* Barra de progreso */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Progreso</span>
                      <span className="font-bold text-gray-900">{stats.percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-green-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${stats.percentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Números */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-gray-900">{stats.totalTickets}</p>
                      <p className="text-xs text-gray-600 mt-1">Total</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{stats.checkedIn}</p>
                      <p className="text-xs text-gray-600 mt-1">Registrados</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
                      <p className="text-xs text-gray-600 mt-1">Pendientes</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No hay datos disponibles</p>
              )}
            </div>

            {/* Últimos check-ins */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600" />
                Últimos Registros
              </h2>

              {stats?.recentCheckIns && stats.recentCheckIns.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {stats.recentCheckIns.map((checkIn, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-green-500 bg-green-50 p-3 rounded"
                    >
                      <p className="font-semibold text-gray-900 text-sm">{checkIn.customerName}</p>
                      <p className="text-xs text-gray-600">
                        {checkIn.seatIdentifier} •{' '}
                        {new Date(checkIn.checkedInAt).toLocaleTimeString('es-MX')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Aún no hay registros</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modal de resultado - Renderizado a nivel de página para persistir */}
      <CheckInResultModal
        isOpen={modalState.isOpen}
        status={modalState.status}
        message={modalState.message}
        ticket={modalState.ticket}
        onClose={handleCloseModal}
        onScanNext={handleScanNext}
        onRetry={handleRetry}
        autoCloseTimeout={0} // Sin auto-close, solo por acción del usuario
      />
    </div>
  );
}

